import React, { useContext, useEffect, useState } from 'react'
import { Card, Form, Button, Spinner, Alert, Modal, InputGroup, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { getAllOrgSettings, updateOrgSetting, getSetupGroups } from '../generic/crud'
import BedRockContext from '../../BedRockContext'
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from '../../utils/toast/Toast'

/**
 * Organization Settings Page
 * Compact and professional settings management
 */
const OrganizationSettingsPage = () => {
    const myData = useContext(BedRockContext)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [settings, setSettings] = useState([])
    const [error, setError] = useState(null)
    const [saving, setSaving] = useState({})

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false)
    const [editingSetting, setEditingSetting] = useState(null)
    const [editValue, setEditValue] = useState('')

    // Setup groups summary (camera + weighbridge editing now lives in the Setup Groups page)
    const [setupGroupCount, setSetupGroupCount] = useState(null)

    // Reset confirmation modal state
    const [showResetModal, setShowResetModal] = useState(false)
    const [resettingSetting, setResettingSetting] = useState(null)


    // Reset toolbar buttons when entering this page
    useEffect(() => {
        myData.setBaseData({ toolBarButtons: [] })
    }, [])

    // Fetch settings on component mount
    useEffect(() => {
        fetchSettings()
        fetchSetupGroupCount()
    }, [])

    // Fetch setup group count for the read-only camera summary card
    const fetchSetupGroupCount = async () => {
        try {
            const response = await getSetupGroups()
            setSetupGroupCount((response.data || []).length)
        } catch (err) {
            console.error('Error fetching setup groups:', err)
            setSetupGroupCount(null)
        }
    }

    // Fetch all settings from API
    const fetchSettings = async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await getAllOrgSettings()
            setSettings(response.data || [])
        } catch (err) {
            console.error('Error fetching settings:', err)
            setError('Failed to fetch settings. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    // Open edit modal
    const openEditModal = (setting) => {
        setEditingSetting(setting)
        setEditValue(setting.value !== null && setting.value !== undefined ? String(setting.value) : '')
        setShowEditModal(true)
    }

    // Close edit modal
    const closeEditModal = () => {
        setShowEditModal(false)
        setEditingSetting(null)
        setEditValue('')
    }

    // Format camera URL for display
    const formatCameraDisplay = (value) => {
        if (!value || typeof value !== 'object') {
            return <span className="text-muted fst-italic small">Not configured</span>
        }
        const { protocol, host, port, username, path } = value
        if (!host) {
            return <span className="text-muted fst-italic small">Not configured</span>
        }
        const portDisplay = port ? `:${port}` : ''
        const userDisplay = username ? `${username}@` : ''
        const pathDisplay = path || ''
        return (
            <span className="text-truncate d-inline-block small" style={{ maxWidth: '300px' }}>
                {protocol}://{userDisplay}{host}{portDisplay}{pathDisplay}
            </span>
        )
    }

    // Open reset confirmation modal
    const openResetModal = (setting) => {
        setResettingSetting(setting)
        setShowResetModal(true)
    }

    // Close reset confirmation modal
    const closeResetModal = () => {
        setShowResetModal(false)
        setResettingSetting(null)
    }

    // Handle reset to 0
    const handleResetSetting = async () => {
        if (!resettingSetting) return

        try {
            setSaving(prev => ({ ...prev, [resettingSetting.typeName]: true }))
            await updateOrgSetting(resettingSetting.typeName, 0)
            showToast('Setting reset to 0', TOAST_SUCCESS)
            closeResetModal()
            await fetchSettings()
        } catch (err) {
            console.error('Error resetting setting:', err)
            showToast(err.response?.data?.error || 'Failed to reset setting', TOAST_FAILURE)
        } finally {
            setSaving(prev => ({ ...prev, [resettingSetting.typeName]: false }))
        }
    }

    // Save setting
    const handleSaveSetting = async () => {
        if (!editingSetting) return

        try {
            setSaving(prev => ({ ...prev, [editingSetting.typeName]: true }))

            let valueToSave = editValue
            if (isNumberSetting(editingSetting.typeName)) {
                valueToSave = parseInt(editValue, 10) || 0
            }

            await updateOrgSetting(editingSetting.typeName, valueToSave)
            showToast('Setting updated successfully', TOAST_SUCCESS)
            closeEditModal()
            await fetchSettings()
        } catch (err) {
            console.error('Error saving setting:', err)
            showToast(err.response?.data?.error || 'Failed to save setting', TOAST_FAILURE)
        } finally {
            setSaving(prev => ({ ...prev, [editingSetting.typeName]: false }))
        }
    }

    // Check if setting is a boolean type
    const isBooleanSetting = (typeName) => {
        return ['SHOW_IMAGES_IN_PRINT', 'CREATE_INVOICE_BY_DEFAULT', 'SHOW_INVOICE_CREATION_SELECTION', 'ENABLE_WEIGHBRIDGE_CAPTURE', 'ENABLE_CAMERA_CAPTURE', 'ALLOW_PRICE_EDIT_PRODUCT_ITEMS', 'ALLOW_PRICE_EDIT_SERVICE_ITEMS', 'ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS', 'PURCHASE_DROP_LOCATION', 'ALLOW_OPENING_BALANCE_ON_CLIENT', 'ALLOW_MANUAL_EMPTY_WEIGHT', 'HIDE_AMOUNT_IN_SALE_RECEIPT', 'HIDE_WIFI_ON_LOGIN_SCREEN', 'HIDE_POWER_BUTTONS_ON_LOGIN_SCREEN', 'ALLOW_SALES_CORRECTION'].includes(typeName)
    }

    // Settings that can only be changed from the server, not on the client
    const isServerOnlySetting = (typeName) => {
        return ['SHOW_IMAGES_IN_PRINT', 'CREATE_INVOICE_BY_DEFAULT', 'SHOW_INVOICE_CREATION_SELECTION', 'ENABLE_WEIGHBRIDGE_CAPTURE', 'ENABLE_CAMERA_CAPTURE', 'ALLOW_PRICE_EDIT_PRODUCT_ITEMS', 'ALLOW_PRICE_EDIT_SERVICE_ITEMS', 'ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS', 'PURCHASE_DROP_LOCATION', 'ALLOW_OPENING_BALANCE_ON_CLIENT', 'ALLOW_MANUAL_EMPTY_WEIGHT', 'ROUND_OFF_MODE', 'HIDE_AMOUNT_IN_SALE_RECEIPT', 'ALLOW_SALES_CORRECTION'].includes(typeName)
    }

    // Check if setting is a dropdown/choice type (label-backed value)
    const isDropdownSetting = (typeName) => {
        return ['ROUND_OFF_MODE'].includes(typeName)
    }

    // Get the human-readable label for a dropdown value
    const getDropdownOptionLabel = (typeName, value) => {
        const options = {
            'ROUND_OFF_MODE': {
                'CEILING': 'Always Round Up (e.g., 33.33 → 34.00)',
                'NEAREST': 'Round to Nearest (e.g., 33.33 → 33.00, 33.77 → 34.00)',
            },
        }
        return options[typeName]?.[value] || (value != null ? String(value) : '')
    }

    // Toggle boolean setting
    const handleToggleBooleanSetting = async (setting) => {
        try {
            setSaving(prev => ({ ...prev, [setting.typeName]: true }))
            const newValue = !(setting.value === true || setting.value === 'true')
            await updateOrgSetting(setting.typeName, newValue)
            showToast('Setting updated successfully', TOAST_SUCCESS)
            await fetchSettings()
        } catch (err) {
            console.error('Error toggling setting:', err)
            showToast('Failed to update setting', TOAST_FAILURE)
        } finally {
            setSaving(prev => ({ ...prev, [setting.typeName]: false }))
        }
    }

    // Check if setting is a number type
    const isNumberSetting = (typeName) => {
        return ['SALE_TICKET_NUMBER_RUNNING_NUMBER', 'PURCHASE_TICKET_NUMBER_RUNNING_NUMBER'].includes(typeName)
    }

    // Check if setting is a camera URL type
    const isCameraSetting = (typeName) => {
        return ['WEIGHING_SCALE_CAMERA_URL'].includes(typeName)
    }

    // Get setting configuration
    const getSettingConfig = (typeName) => {
        const configs = {
            'SALE_TICKET_NUMBER_RUNNING_NUMBER': {
                icon: 'bi-receipt', color: '#0d6efd', label: 'Sale Ticket Number',
                description: 'Tracks the current sale ticket counter. The next sale will use this number + 1 as its ticket number.'
            },
            'PURCHASE_TICKET_NUMBER_RUNNING_NUMBER': {
                icon: 'bi-truck', color: '#6610f2', label: 'Purchase Ticket Number',
                description: 'Tracks the current purchase ticket counter. The next purchase will use this number + 1 as its ticket number.'
            },
            'WEIGHING_SCALE_CAMERA_URL': {
                icon: 'bi-camera-video', color: '#6f42c1', label: 'Weighing Scale Camera',
                description: 'Camera URL used to capture vehicle images at the weighbridge during sales and purchases.'
            },
            'SHOW_IMAGES_IN_PRINT': {
                icon: 'bi-printer', color: '#20c997', label: 'Show Images in Print',
                description: 'When enabled, captured vehicle images (empty & loaded weight) will be included in the printed sale/purchase ticket.'
            },
            'CREATE_INVOICE_BY_DEFAULT': {
                icon: 'bi-receipt-cutoff', color: '#198754', label: 'Create Invoice by Default',
                description: 'When enabled, the "Create Invoice" toggle will be pre-selected for every new sale, applying tax to all items automatically.'
            },
            'SHOW_INVOICE_CREATION_SELECTION': {
                icon: 'bi-ui-checks', color: '#0dcaf0', label: 'Show Invoice Creation Selection',
                description: 'When enabled, the invoice creation toggle will be shown in the Sales Entry page, allowing the operator to choose whether to create an invoice.'
            },
            'ENABLE_WEIGHBRIDGE_CAPTURE': {
                icon: 'bi-speedometer2', color: '#0d6efd', label: 'Enable Weighbridge Capture',
                description: 'When enabled, weight is captured automatically from the weighbridge. When disabled, operators can manually enter Empty and Loaded weights.'
            },
            'ENABLE_CAMERA_CAPTURE': {
                icon: 'bi-camera-video', color: '#6f42c1', label: 'Enable Camera Capture',
                description: 'When enabled, vehicle image capture is required during sale entry. When disabled, sales can proceed without capturing a vehicle image.'
            },
            'ALLOW_PRICE_EDIT_PRODUCT_ITEMS': {
                icon: 'bi-currency-rupee', color: '#e83e8c', label: 'Allow Price Edit for Product Items',
                description: 'When enabled, operators can edit the price of product items during sale entry. When disabled, product prices are locked to the predefined rate.'
            },
            'ALLOW_PRICE_EDIT_SERVICE_ITEMS': {
                icon: 'bi-currency-rupee', color: '#fd7e14', label: 'Allow Price Edit for Service Items',
                description: 'When enabled, operators can edit the price of service items during sale entry. When disabled, service prices are locked to the predefined rate.'
            },
            'ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS': {
                icon: 'bi-currency-rupee', color: '#6610f2', label: 'Allow Price Edit for Product Items',
                description: 'When enabled, operators can edit the price of product items during purchase entry. When disabled, product prices are locked to the predefined rate.'
            },
            'PURCHASE_DROP_LOCATION': {
                icon: 'bi-geo-alt', color: '#dc3545', label: 'Purchase Dump Location',
                description: 'When enabled, operators can specify the dump location (Crusher or Stock Yard) during purchase entry.'
            },
            'ALLOW_OPENING_BALANCE_ON_CLIENT': {
                icon: 'bi-wallet2', color: '#0d6efd', label: 'Allow Opening Balance on Client',
                description: 'When enabled, operators can enter opening balance while creating customers and suppliers. This setting can only be changed from the server.'
            },
            'ALLOW_MANUAL_EMPTY_WEIGHT': {
                icon: 'bi-truck', color: '#198754', label: 'Allow Manual Empty Weight on Client',
                description: 'When enabled, operators can manually enter the empty weight while creating a new vehicle. This setting can only be changed from the server.'
            },
            'ROUND_OFF_MODE': {
                icon: 'bi-calculator', color: '#198754', label: 'Sale Round Off Mode',
                description: 'Controls how the final sale total is rounded. "Always Round Up" rounds any fractional amount up to the next whole rupee. "Round to Nearest" rounds down when the decimal part is below 0.50 and up when it is 0.50 or above. This setting can only be changed from the server.'
            },
            'HIDE_AMOUNT_IN_SALE_RECEIPT': {
                icon: 'bi-eye-slash', color: '#dc3545', label: 'Hide Amount in Sale Receipt',
                description: 'When enabled, the printed sale receipt hides all money-related information (rate, discount, tax, amount, totals). Only item descriptions and quantities are shown. This setting can only be changed from the server.'
            },
            'HIDE_WIFI_ON_LOGIN_SCREEN': {
                icon: 'bi-wifi-off', color: '#dc3545', label: 'Hide WiFi Setup on Login/Setup Screens',
                description: 'When enabled, the WiFi status indicator and Configure Network dialog are hidden on the Login and Organization Setup screens. Toggle this locally or from the server — the value syncs both ways.'
            },
            'HIDE_POWER_BUTTONS_ON_LOGIN_SCREEN': {
                icon: 'bi-power', color: '#dc3545', label: 'Hide Power Buttons on Login Screen',
                description: 'When enabled, the Restart Device and Shutdown Device buttons are hidden on the Login screen. Toggle this locally or from the server — the value syncs both ways.'
            },
            'ALLOW_SALES_CORRECTION': {
                icon: 'bi-plus-circle', color: '#198754', label: 'Allow Weight Correction in Sales Orders',
                description: 'When enabled, operators can enter a Weight Correction during sale entry that is added to the loaded weight (upward adjustment). This setting can only be changed from the server.'
            },
        }
        return configs[typeName] || { icon: 'bi-gear', color: '#6c757d', label: typeName, description: '' }
    }

    // Format display value
    const formatDisplayValue = (setting) => {
        if (isCameraSetting(setting.typeName)) {
            return formatCameraDisplay(setting.value)
        }

        if (isBooleanSetting(setting.typeName)) {
            const isEnabled = setting.value === true || setting.value === 'true'
            return (
                <span className={`badge ${isEnabled ? 'bg-success' : 'bg-secondary'} bg-opacity-10 ${isEnabled ? 'text-success' : 'text-secondary'}`}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
            )
        }

        if (isDropdownSetting(setting.typeName)) {
            const label = getDropdownOptionLabel(setting.typeName, setting.value)
            return label
                ? <span className="badge bg-primary bg-opacity-10 text-primary">{label}</span>
                : <span className="text-muted fst-italic small">Not configured</span>
        }

        if (setting.value === null || setting.value === undefined || setting.value === '') {
            return <span className="text-muted fst-italic small">Not configured</span>
        }

        if (isNumberSetting(setting.typeName)) {
            return <code className="fs-6">{setting.value}</code>
        }

        return <span>{String(setting.value)}</span>
    }

    // Get input type for setting
    const getInputType = (typeName) => {
        if (isNumberSetting(typeName)) return 'number'
        if (isCameraSetting(typeName)) return 'url'
        return 'text'
    }

    // Get help text for setting
    const getHelpText = (typeName) => {
        const helpTexts = {
            'SALE_TICKET_NUMBER_RUNNING_NUMBER': 'Next sale ticket will use this number + 1',
            'PURCHASE_TICKET_NUMBER_RUNNING_NUMBER': 'Next purchase ticket will use this number + 1',
            'WEIGHING_SCALE_CAMERA_URL': 'e.g., rtsp://camera-ip:port/stream',
            'SHOW_IMAGES_IN_PRINT': 'Include captured images when printing tickets',
            'CREATE_INVOICE_BY_DEFAULT': 'Pre-select the Create Invoice checkbox when creating a new sale',
            'SHOW_INVOICE_CREATION_SELECTION': 'Show or hide the invoice creation toggle in the Sales Entry page',
            'ENABLE_WEIGHBRIDGE_CAPTURE': 'Turn off if the weighbridge is non-operational to allow manual weight entry',
            'ENABLE_CAMERA_CAPTURE': 'Turn off if the camera is non-operational to allow sales without vehicle images',
            'ALLOW_PRICE_EDIT_PRODUCT_ITEMS': 'Allow operators to change product item prices during sale entry',
            'ALLOW_PRICE_EDIT_SERVICE_ITEMS': 'Allow operators to change service item prices during sale entry',
            'ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS': 'Allow operators to change product item prices during purchase entry',
            'PURCHASE_DROP_LOCATION': 'Allow operators to specify dump location during purchase entry',
            'ALLOW_OPENING_BALANCE_ON_CLIENT': 'Allow operators to enter opening balance for customers/suppliers',
            'ALLOW_MANUAL_EMPTY_WEIGHT': 'Allow operators to manually enter empty weight during vehicle creation',
            'ALLOW_SALES_CORRECTION': 'Allow operators to apply a Weight Correction added to the loaded weight during sale entry',
        }
        return helpTexts[typeName] || ''
    }

    // Known setting types to display in the UI
    const isKnownSetting = (typeName) => {
        return isNumberSetting(typeName) || isCameraSetting(typeName) || isBooleanSetting(typeName) || isDropdownSetting(typeName)
    }

    // Check if setting is a sale/invoice preference
    const isSalePreferenceSetting = (typeName) => {
        return ['CREATE_INVOICE_BY_DEFAULT', 'SHOW_INVOICE_CREATION_SELECTION', 'ALLOW_PRICE_EDIT_PRODUCT_ITEMS', 'ALLOW_PRICE_EDIT_SERVICE_ITEMS', 'ROUND_OFF_MODE', 'HIDE_AMOUNT_IN_SALE_RECEIPT', 'ALLOW_SALES_CORRECTION'].includes(typeName)
    }

    // Check if setting is a purchase preference
    const isPurchasePreferenceSetting = (typeName) => {
        return ['ALLOW_PRICE_EDIT_PURCHASE_PRODUCT_ITEMS', 'PURCHASE_DROP_LOCATION'].includes(typeName)
    }

    // Check if setting is a device/operational setting
    const isDeviceSetting = (typeName) => {
        return ['SHOW_IMAGES_IN_PRINT', 'ENABLE_WEIGHBRIDGE_CAPTURE', 'ENABLE_CAMERA_CAPTURE', 'HIDE_WIFI_ON_LOGIN_SCREEN', 'HIDE_POWER_BUTTONS_ON_LOGIN_SCREEN'].includes(typeName)
    }

    // Check if setting is a client preference
    const isClientPreferenceSetting = (typeName) => {
        return ['ALLOW_OPENING_BALANCE_ON_CLIENT', 'ALLOW_MANUAL_EMPTY_WEIGHT'].includes(typeName)
    }

    // Group settings by category (only show known settings)
    const groupSettings = () => {
        const known = settings.filter(s => isKnownSetting(s.typeName))
        const numberSettings = known.filter(s => isNumberSetting(s.typeName))
        const salePreferenceSettings = known.filter(s => isSalePreferenceSetting(s.typeName))
        const purchasePreferenceSettings = known.filter(s => isPurchasePreferenceSetting(s.typeName))
        const deviceSettings = known.filter(s => isDeviceSetting(s.typeName))
        const clientPreferenceSettings = known.filter(s => isClientPreferenceSetting(s.typeName))
        return { numberSettings, salePreferenceSettings, purchasePreferenceSettings, deviceSettings, clientPreferenceSettings, otherSettings: [] }
    }

    // Render settings table
    const renderSettingsTable = (settingsList, title, icon) => {
        if (settingsList.length === 0) return null

        return (
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="bg-white py-3 px-4 border-bottom">
                    <div className="d-flex align-items-center">
                        <i className={`bi ${icon} me-2 text-primary`}></i>
                        <span className="fw-semibold">{title}</span>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table className="mb-0" hover>
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4 py-2 fw-semibold text-muted small" style={{ width: '55%' }}>Setting</th>
                                <th className="py-2 fw-semibold text-muted small" style={{ width: '25%' }}>Value</th>
                                <th className="pe-4 py-2 fw-semibold text-muted small text-end" style={{ width: '20%' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {settingsList.map(setting => {
                                const config = getSettingConfig(setting.typeName)
                                const isResetable = ['SALE_TICKET_NUMBER_RUNNING_NUMBER', 'PURCHASE_TICKET_NUMBER_RUNNING_NUMBER'].includes(setting.typeName)
                                return (
                                    <tr key={setting.typeName}>
                                        <td className="ps-4 py-3 align-middle">
                                            <div className="d-flex align-items-start">
                                                <i className={`bi ${config.icon} me-2 mt-1`} style={{ color: config.color }}></i>
                                                <div>
                                                    <span className="fw-medium">{config.label}</span>
                                                    {config.description && (
                                                        <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.3', marginTop: '2px' }}>
                                                            {config.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 align-middle">
                                            {formatDisplayValue(setting)}
                                        </td>
                                        <td className="pe-4 py-3 align-middle text-end">
                                            {isServerOnlySetting(setting.typeName) ? (
                                                <span className="text-muted small d-inline-flex align-items-center gap-1">
                                                    <i className="bi bi-lock"></i>
                                                    Server only
                                                </span>
                                            ) : isBooleanSetting(setting.typeName) ? (
                                                <Form.Check
                                                    type="switch"
                                                    checked={setting.value === true || setting.value === 'true'}
                                                    onChange={() => handleToggleBooleanSetting(setting)}
                                                    disabled={saving[setting.typeName]}
                                                    className="d-inline-block"
                                                />
                                            ) : isResetable ? (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-warning"
                                                    onClick={() => openResetModal(setting)}
                                                    disabled={saving[setting.typeName]}
                                                >
                                                    {saving[setting.typeName] ? (
                                                        <Spinner size="sm" animation="border" />
                                                    ) : (
                                                        <><i className="bi bi-arrow-counterclockwise me-1"></i>Reset</>
                                                    )}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="p-0 text-primary"
                                                    onClick={() => openEditModal(setting)}
                                                    disabled={saving[setting.typeName]}
                                                >
                                                    {saving[setting.typeName] ? (
                                                        <Spinner size="sm" animation="border" />
                                                    ) : (
                                                        <><i className="bi bi-pencil-square me-1"></i>Edit</>
                                                    )}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        )
    }

    // Render edit modal
    const renderEditModal = () => {
        if (!editingSetting) return null
        const config = getSettingConfig(editingSetting.typeName)

        return (
            <Modal show={showEditModal} onHide={closeEditModal} centered size="sm">
                <Modal.Header closeButton className="py-3 px-4 border-bottom">
                    <Modal.Title className="fs-6">
                        <i className={`bi ${config.icon} me-2`} style={{ color: config.color }}></i>
                        {config.label}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    <Form.Group>
                        <Form.Label className="small fw-semibold text-muted mb-1">Value</Form.Label>
                        <InputGroup size="sm">
                            {isNumberSetting(editingSetting.typeName) && (
                                <InputGroup.Text><i className="bi bi-hash"></i></InputGroup.Text>
                            )}
                            {isCameraSetting(editingSetting.typeName) && (
                                <InputGroup.Text><i className="bi bi-link-45deg"></i></InputGroup.Text>
                            )}
                            <Form.Control
                                type={getInputType(editingSetting.typeName)}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                placeholder="Enter value"
                                min={isNumberSetting(editingSetting.typeName) ? 0 : undefined}
                                autoFocus
                            />
                        </InputGroup>
                        <Form.Text className="text-muted small">
                            {getHelpText(editingSetting.typeName)}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="py-2 px-4 border-top">
                    <Button variant="light" size="sm" onClick={closeEditModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSaveSetting}
                        disabled={saving[editingSetting?.typeName]}
                    >
                        {saving[editingSetting?.typeName] ? (
                            <><Spinner size="sm" animation="border" className="me-1" />Saving</>
                        ) : (
                            'Save'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        )
    }

    // Render reset confirmation modal
    const renderResetModal = () => {
        if (!resettingSetting) return null
        const config = getSettingConfig(resettingSetting.typeName)

        return (
            <Modal show={showResetModal} onHide={closeResetModal} centered size="sm">
                <Modal.Header closeButton className="py-3 px-4 border-bottom bg-warning bg-opacity-10">
                    <Modal.Title className="fs-6">
                        <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                        Reset {config.label}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    <p className="small mb-2">Reset counter to <strong>0</strong>?</p>
                    <p className="text-muted small mb-0">Next ticket will be numbered 0001.</p>
                </Modal.Body>
                <Modal.Footer className="py-2 px-4 border-top">
                    <Button variant="light" size="sm" onClick={closeResetModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="warning"
                        size="sm"
                        onClick={handleResetSetting}
                        disabled={saving[resettingSetting?.typeName]}
                    >
                        {saving[resettingSetting?.typeName] ? (
                            <><Spinner size="sm" animation="border" className="me-1" />Resetting</>
                        ) : (
                            'Reset'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        )
    }

    const breadcrumbs = [
        { title: 'Home', path: '/dashboard', isSeparator: false, isActive: false },
        { title: '', path: '', isSeparator: true, isActive: false },
    ]

    const { numberSettings, salePreferenceSettings, purchasePreferenceSettings, deviceSettings, clientPreferenceSettings, otherSettings } = groupSettings()

    return (
        <>
            <PageTitle breadcrumbs={breadcrumbs}>Organization Settings</PageTitle>
            <div className="px-4 px-lg-6">
                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">Settings</h4>
                        <p className="text-muted small mb-0">Configure organization-wide preferences</p>
                    </div>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={fetchSettings}
                        disabled={loading}
                    >
                        <i className={`bi bi-arrow-repeat ${loading ? 'spin' : ''}`}></i>
                    </Button>
                </div>

                {/* Loading State */}
                {loading && settings.length === 0 && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="text-muted small mt-2 mb-0">Loading settings...</p>
                    </div>
                )}

                {/* Empty State */}
                {!loading && settings.length === 0 && (
                    <Alert variant="light" className="border py-3">
                        <i className="bi bi-info-circle me-2"></i>
                        No settings configured yet.
                    </Alert>
                )}

                {/* Settings Tables */}
                {renderSettingsTable(numberSettings, 'Running Numbers', 'bi-123')}
                {renderSettingsTable(salePreferenceSettings, 'Sale Preferences', 'bi-receipt')}
                {renderSettingsTable(purchasePreferenceSettings, 'Purchase Preferences', 'bi-cart4')}
                {renderSettingsTable(deviceSettings, 'Device & Print Settings', 'bi-toggles')}
                {renderSettingsTable(clientPreferenceSettings, 'Client Preferences', 'bi-phone')}
                {renderSettingsTable(otherSettings, 'Other Settings', 'bi-gear')}

                {/* Hardware — Setup Groups (camera + weighbridge editing lives on the Setup Groups page) */}
                <Card className="mb-4 border-0 shadow-sm">
                    <Card.Header className="bg-white py-3 px-4 border-bottom">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-speedometer2 me-2 text-primary"></i>
                            <span className="fw-semibold">Hardware</span>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                                    <i className="bi bi-camera-video fs-3 text-primary"></i>
                                </div>
                                <div>
                                    <h6 className="mb-1">Weighing Scale Camera &amp; Weighbridge</h6>
                                    <p className="text-muted small mb-0">
                                        {setupGroupCount === null
                                            ? 'Configure your cameras and weighbridges as Setup Groups.'
                                            : setupGroupCount === 0
                                                ? 'No setup groups configured yet.'
                                                : `${setupGroupCount} setup group${setupGroupCount > 1 ? 's' : ''} configured — each pairs one camera with one weighbridge.`}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => navigate('/weighbridge-setup')}
                            >
                                <i className="bi bi-gear me-1"></i>
                                Manage Setup Groups
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {/* Edit Modal */}
                {renderEditModal()}

                {/* Reset Modal */}
                {renderResetModal()}

            </div>

            <style>{`
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .table > :not(caption) > * > * { border-bottom-color: #f1f1f1; }
            `}</style>
        </>
    )
}

export { OrganizationSettingsPage }
